/* eslint-disable no-restricted-properties, no-useless-escape */
import parsePhoneNumber from 'libphonenumber-js';
import { camelCase, isFinite, isNil, random, snakeCase, uniqBy } from 'lodash';

import type { Metrics, MixedSelectDropdownItem, StatusLabelType } from 'types';
import type { HelperAbbrNumOptions, HelperSafeAbbrNumOptions } from 'types/Helper.type';
import type { Phone } from 'modules/shipping/components/ShippingStepPhoneForm';

type RGB = {
  r?: number,
  g?: number,
  b?: number
};

const PREFIX_REGEX = /[#@]/;
export const DEFAULT_COUNTRY_CODE = '+49';
export const DEFAULT_COUNTRY_ISO = 'DE';

export const STATUS_LABEL_MAP: Record<string, StatusLabelType> = {
  approved: 'success',
  pending: 'warning',
  declined: 'danger',
  not_required: 'neutral',
  ended: 'neutral',
  published: 'success',
  unpublished: 'neutral'
};

export function getNumberOfDecimals(value: number): 0 | 1 | 2 {
  if (value > 0 && value < 10) {
    return 2;
  }

  if (value >= 10 && value < 1000) {
    return 1;
  }

  return 0;
}

export function replaceDecimalWithComma(number: number | string, separator: string = ','): string {
  return number ? number.toString().replace('.', separator) : '0';
}

export function addThousandsSeparator(number: number | string): string {
  return number.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
}

// return you a string which is 1.2K
export function abbrNum(
  number: number | string | undefined | null,
  options?: HelperAbbrNumOptions | null,
  allowDecimalPlaces: boolean = false
): string {
  // Early exit for nil inputs and numbers that cannot be type-casted
  if (isNil(number) || window.isNaN(Number(number)) || !isFinite(Number(number))) {
    return '';
  }

  const defaultOptions: HelperSafeAbbrNumOptions = {
    // 2 decimal places => 100, 3 => 1000, etc
    decimal: 2,
    // Whether the k, m, b, t should be in upper case
    upperCase: true,
    // The separator for number. Either . or ,
    separator: '.'
  };
  const mergedOptions: HelperSafeAbbrNumOptions = { ...defaultOptions, ...options };

  const decPlaces = 10 ** mergedOptions.decimal;
  // Enumerate number abbreviations
  const abbrev = ['', 'k', 'm', 'b', 't'];
  const absNumber = Math.abs(Number(number));
  const sign = Math.sign(Number(number));

  if (Number(number) > -1000 && Number(number) < 1000) {
    // all numbers within this range were returned as is previously without any abbreviation
    const smallFormattedNumber = (sign * Math.round(absNumber * decPlaces) / decPlaces);
    return replaceDecimalWithComma(smallFormattedNumber, mergedOptions.separator);
  }

  // find closes power of 1000 to get index for abbreviation
  const closestPowOf1000 = Math.floor(Math.log10(absNumber) / 3);
  // get abbrev index and clamp to fit supported abbreviations
  let abbrevIndex = Math.min(closestPowOf1000, abbrev.length - 1);

  // Convert array index to "1000", "1000000", etc
  const size = 10 ** (abbrevIndex * 3);

  // Here, we multiply by decPlaces, round, and then divide by decPlaces.
  // This gives us nice rounding to a particular decimal place.
  let roundedNumber = Math.round(absNumber * decPlaces / size) / decPlaces;

  // when appropriate, this rounds off the decimal (COLLAB-1954)
  if (!allowDecimalPlaces && String(roundedNumber).substr(1, 1) !== '.') {
    roundedNumber = Math.round(roundedNumber);
  }

  // Handle special case where we round up to the next abbreviation
  if ((roundedNumber === 1000) && (abbrevIndex < abbrev.length - 1)) {
    roundedNumber = 1;
    abbrevIndex++;
  }

  // Add the letter for the abbreviation and format final string
  let formattedNumber = (sign * roundedNumber) + abbrev[abbrevIndex];
  formattedNumber = replaceDecimalWithComma(formattedNumber, mergedOptions.separator);

  // quick hack to prevent 'k' from being capitalized
  // per Hanna on 26 Oct 2017
  if (formattedNumber && formattedNumber.includes('k')) {
    return formattedNumber;
  }

  return formattedNumber && mergedOptions.upperCase ? formattedNumber.toUpperCase() : formattedNumber;
}

export function getMetricValueInPercentage(
  value: number | string | null | undefined,
  options: { defaultValue?: string, hasThousandsSeparator?: boolean} = { hasThousandsSeparator: true }) {
  if (isNil(value) || isNaN(Number(value))) {
    return (options && options.defaultValue) || 'N/A';
  }

  let modifiedValue = value;
  const noOfRequiredDecimalPlaces = getNumberOfDecimals(Math.abs(Number(value)));

  if (options.hasThousandsSeparator || typeof options.hasThousandsSeparator !== 'boolean') {
    modifiedValue = addThousandsSeparator((Math.round((Number(modifiedValue)) * 100) / 100).toFixed(noOfRequiredDecimalPlaces));
  } else {
    modifiedValue = Number(modifiedValue).toFixed(noOfRequiredDecimalPlaces);
  }

  return `${modifiedValue}%`;
}

export function getRawMetricValue(metrics: Metrics, key: string): number | undefined | null {
  return metrics[camelCase(key)];
}

export function getMetricValue(metrics: Metrics, key: string): number {
  return getRawMetricValue(metrics, camelCase(key)) || 0;
}

export function convertRatio(decimal: number | string | undefined | null): number {
  if (isNil(decimal)) {
    return 0;
  }

  let value = Number(decimal) * 100;
  value = Number(value.toFixed(1));

  if (value % 1 === 0) {
    return Number(value.toFixed(0));
  }
  return value;
}

export function stripHTML(content: string): string {
  return content.replace(/<\/?[^>]+(>|$)/g, '').trim();
}

export function convertStringToBoolean(str: string): boolean | string {
  return str === 'true' || (str === 'false' ? false : str);
}

// `selector` needs to include `#` or `.` (e.g. #my-id, .my-class)
export function copyTextToClipboard(selector: string = '', autoSelectInput: boolean = false): boolean {
  const element = document.querySelector(selector);
  const selection = getSelection();
  const range = document.createRange();

  if (!selection || !element) {
    return false;
  }

  // clear existing selections before starting
  selection.removeAllRanges();

  range.selectNodeContents(element);
  selection.addRange(range);

  if (autoSelectInput && element instanceof HTMLInputElement) {
    // input text can not be selected with 'selectNodeContents'
    element.select();
  }

  document.execCommand('copy');

  // clear selection so user won't notice that the text was selected
  selection.removeAllRanges();
  return true;
}

export function expandDataProps(data: any) {
  const dataProps = {};

  if (data) {
    Object.keys(data).forEach(attr => {
      dataProps[`data-${attr}`] = data[attr];
    });
  }
  return dataProps;
}

export function replaceString(str: string, oldValue: string, newValue: string): string {
  const re = new RegExp(oldValue, 'g');
  return str.replace(re, newValue);
}

export const getPrefix = (value: string): string => {
  const prefix = value.substring(0, 1);

  return PREFIX_REGEX.test(prefix) ? prefix : '';
};

export const stripPrefix = (value: string): string => {
  const prefix = value.substring(0, 1);

  return PREFIX_REGEX.test(prefix) ? value.substring(1) : value;
};

export const getRandomItemFromArray = (items: Array<any>): any => items[random(items.length - 1)];

export const getPercentage = (
  value: number | undefined | null,
  maxValue: number,
  withRounding: boolean = true
): number => {
  if (!value) {
    return 0;
  }

  let updatedValue = (value / maxValue) * 100;

  if (withRounding) {
    updatedValue = Math.round((updatedValue + Number.EPSILON) * 100) / 100;
  }

  return updatedValue;
};

export const splitStringByLastSpace = (input: string): [string, string] => {
  const parts = input.split(' ');
  const firstWords = parts.length > 1 ? parts.slice(0, parts.length - 1).join(' ') : '';
  const last = parts.length > 1 ? parts.slice(parts.length - 1, parts.length)[0] : parts[0];
  return [firstWords, last];
};

// https://github.com/visjs/vis-util/blob/master/src/util.ts#L916
export const hexToRGB = (hex: string = ''): RGB => {
  const fullHexRE = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i;
  const shortHexRE = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
  let result;

  switch (hex.length) {
    case 3:
    case 4:
      result = shortHexRE.exec(hex);
      return result
        ? {
          r: parseInt(result[1] + result[1], 16),
          g: parseInt(result[2] + result[2], 16),
          b: parseInt(result[3] + result[3], 16)
        }
        : {};
    case 6:
    case 7:
      result = fullHexRE.exec(hex);
      return result
        ? {
          r: parseInt(result[1], 16),
          g: parseInt(result[2], 16),
          b: parseInt(result[3], 16)
        }
        : {};
    default:
      return {};
  }
};

// eslint-disable-next-line no-promise-executor-return
export const wait = async (miliseconds: number): Promise<void> => new Promise(resolve => setTimeout(resolve, miliseconds));

export const getPhone = (phone: string | undefined): Phone => {
  if (phone) {
    const phoneObj = parsePhoneNumber(phone);
    if (phoneObj) {
      return {
        countryCode: `+${phoneObj.countryCallingCode}`,
        number: phoneObj.nationalNumber,
        originalNumber: phone,
        country: phoneObj.country
      };
    }
  }

  return {
    countryCode: DEFAULT_COUNTRY_CODE,
    originalNumber: phone || '',
    number: phone && phone[0] === '0' ? phone.slice(1) : (phone || ''),
    country: 'DE'
  };
};

export const isDefined = function isDefined<T>(param?: T | null): param is T {
  return Boolean(param);
};

export const sortAndUniqueItems = (
  items: Array<MixedSelectDropdownItem>
): Array<MixedSelectDropdownItem> => {
  const safeItems = items || [];
  const uniqueItems = uniqBy(safeItems, 'identifier');

  return uniqueItems.sort((
    a: MixedSelectDropdownItem,
    b: MixedSelectDropdownItem
  ) => (
    (a.label || '').localeCompare(b.label || '')
  ));
};

export const replaceHistoryStateWithDefault = (): void => (
  window.history.replaceState(
    '', document.title, `${window.location.origin}${window.location.pathname}`
  )
);

export const getFileSizeInUnits = (sizeInBytes: number, units: 'kb' | 'mb' | 'gb' = 'mb'): number => {
  if (units === 'kb') {
    return parseInt((sizeInBytes / 1014).toFixed(0), 10);
  }

  if (units === 'mb') {
    return parseInt((sizeInBytes / (1024 * 1024)).toFixed(0), 10);
  }

  return parseInt((sizeInBytes / (1024 * 1024 * 1024)).toFixed(0), 10);
};

export const convertStringToSnakeCase = (country?: string): string => (
  snakeCase(country?.trim())
);

export const convertListToHumanReadable = (
  list: Array<string | number>,
  conjunction: 'and' | 'or' = 'or'
): string => (
  list.join(', ')
    .replace(/, ([^,]*)$/, ` ${conjunction} $1`)
);

export const getMaxAndMinSignedInt32 = (): [number, number] => (
  [(2 ** 31 - 1), ((-2) ** 31)]
);
